import React, { useEffect, useState, useCallback } from "react";
import { Table, Tag, Button, Popconfirm, Typography, message, Modal, Input, Tooltip, Space } from "antd";
import { LaptopOutlined, MobileOutlined, SafetyCertificateOutlined, DeleteOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../variables/api/axiosInstance";
import { getDeviceId } from "../../variables/deviceId";

const { Text } = Typography;

// Écran « Mes appareils » (sécurité par appareil) : liste, renommage, révocation.
const MesAppareils = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [renaming, setRenaming] = useState(null); // device en cours de renommage
  const [labelValue, setLabelValue] = useState("");
  const currentDeviceId = getDeviceId();

  const charger = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get("/api/device/mine")
      .then((res) => setDevices(Array.isArray(res.data) ? res.data : []))
      .catch(() => message.error("Impossible de charger vos appareils."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const revoquer = (deviceId) => {
    axiosInstance
      .delete(`/api/device/${deviceId}`)
      .then(() => {
        message.success("Appareil révoqué.");
        charger();
      })
      .catch(() => message.error("Échec de la révocation."));
  };

  const ouvrirRenommage = (d) => {
    setRenaming(d);
    setLabelValue(d.label || "");
  };

  const enregistrerLabel = () => {
    if (!renaming) return;
    axiosInstance
      .put(`/api/device/${renaming.deviceId}/label`, { label: labelValue })
      .then(() => {
        message.success("Appareil renommé.");
        setRenaming(null);
        charger();
      })
      .catch(() => message.error("Échec du renommage."));
  };

  const iconePour = (os) => {
    const mobile = os && ["iOS", "Android"].includes(os);
    return mobile ? <MobileOutlined /> : <LaptopOutlined />;
  };

  const columns = [
    {
      title: "Appareil",
      key: "appareil",
      render: (_, d) => (
        <Space>
          <span style={{ fontSize: 18, color: "#242166" }}>{iconePour(d.os)}</span>
          <div>
            <div style={{ fontWeight: 600 }}>
              {d.label || `${d.browser || "Navigateur"}${d.os ? " · " + d.os : ""}`}
              {d.deviceId === currentDeviceId && (
                <Tag color="blue" style={{ marginLeft: 8 }}>Cet appareil</Tag>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {(d.browser || "—")}{d.os ? " · " + d.os : ""}{d.ipLast ? " · " + d.ipLast : ""}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Statut",
      key: "statut",
      width: 160,
      render: (_, d) =>
        d.revoked ? (
          <Tag color="red">Révoqué</Tag>
        ) : d.trusted ? (
          <Tag color="green" icon={<SafetyCertificateOutlined />}>De confiance</Tag>
        ) : (
          <Tag color="orange">Non de confiance</Tag>
        ),
    },
    {
      title: "Dernière activité",
      dataIndex: "lastSeenAt",
      key: "lastSeenAt",
      width: 170,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
    },
    {
      title: "Ajouté le",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, d) => (
        <Space>
          <Tooltip title="Renommer">
            <Button size="small" icon={<EditOutlined />} onClick={() => ouvrirRenommage(d)} />
          </Tooltip>
          {!d.revoked && (
            <Popconfirm
              title="Révoquer cet appareil ?"
              description="Il devra à nouveau passer par une vérification à la prochaine connexion."
              okText="Révoquer"
              okType="danger"
              cancelText="Annuler"
              onConfirm={() => revoquer(d.deviceId)}
            >
              <Tooltip title="Révoquer">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="card mb-6" style={{ borderRadius: 12 }}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <SafetyCertificateOutlined style={{ marginRight: 8, color: "#242166" }} />
          Mes appareils
        </h5>
        <Button size="small" icon={<ReloadOutlined />} onClick={charger}>Actualiser</Button>
      </div>
      <div className="card-body pt-3">
        <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
          Les appareils qui se connectent à votre compte. Un appareil « de confiance » ne redemande pas
          de code de vérification. Révoquez tout appareil que vous ne reconnaissez pas.
        </Text>
        <Table
          rowKey="deviceId"
          size="small"
          columns={columns}
          dataSource={devices}
          loading={loading}
          pagination={false}
          scroll={{ x: 800 }}
        />
      </div>

      <Modal
        open={!!renaming}
        title="Renommer l'appareil"
        onOk={enregistrerLabel}
        onCancel={() => setRenaming(null)}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Input
          value={labelValue}
          onChange={(e) => setLabelValue(e.target.value)}
          placeholder="Ex. PC bureau, iPhone perso…"
          maxLength={120}
        />
      </Modal>
    </div>
  );
};

export default MesAppareils;
